import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import employeeSelfService from '../services/employeeSelfService';
import leaveService from '../services/leaveService';
import attendanceService from '../services/attendanceService';
import payrollService from '../services/payrollService';
import notificationService from '../services/notificationService';
import {
  ESS_CLIENT_API_KEY,
  loadClientSidePortal,
  fetchEmployeeLeaves,
  fetchEmployeeDocuments,
  fetchEmployeeAttendance,
  buildLeaveBalance,
} from '../utils/employeeSelfServiceClient';
import { monthDateRange } from '../utils/attendanceHelpers';

const EmployeeSelfServiceContext = createContext(null);

export const useEmployeeSelfService = () => {
  const ctx = useContext(EmployeeSelfServiceContext);
  if (!ctx) {
    throw new Error('useEmployeeSelfService must be used within EmployeeSelfServiceProvider');
  }
  return ctx;
};

export const EmployeeSelfServiceProvider = ({ children }) => {
  const { userProfile } = useAuth();
  const isLocalAuth = !!localStorage.getItem('laborpro_local_auth') || userProfile?.id?.startsWith('local-');

  const [employee, setEmployee] = useState(null);
  const [deployment, setDeployment] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState(null);
  const [clientSideMode, setClientSideMode] = useState(false);

  const [leaves, setLeaves] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [attendanceStats, setAttendanceStats] = useState({ present: 0, absent: 0, late: 0, half_day: 0 });
  const [todayStatus, setTodayStatus] = useState(null);
  const [payslips, setPayslips] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [letters, setLetters] = useState({ offer_letters: [], increment_letters: [] });
  const [notificationCenter, setNotificationCenter] = useState([]);
  const [notificationPrefs, setNotificationPrefs] = useState({
    inApp: true,
    email: false,
    sms: false,
    attendanceAlerts: true,
    payrollAlerts: true,
    leaveUpdates: true,
  });

  const applyPortalData = useCallback((data) => {
    setEmployee(data.employee);
    setDeployment(data.deployment || null);
    setDashboard(data.dashboard || null);
    setLeaves(data.leaves || []);
    setLeaveBalance(data.leaveBalance || []);
    setHolidays(data.holidays || []);
    setAttendanceRecords(data.attendanceRecords || []);
    setAttendanceStats(data.attendanceStats || { present: 0, absent: 0, late: 0, half_day: 0 });
    setTodayStatus(data.todayStatus || null);
    setPayslips(data.payslips || []);
    setDocuments(data.documents || []);
    setLetters(data.letters || { offer_letters: [], increment_letters: [] });
    setNotificationCenter(data.notificationCenter || []);
    setClientSideMode(!!data.clientSideMode);
  }, []);

  const loadAttendance = useCallback(async (employeeId) => {
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
    const result = await attendanceService.getAttendanceRecords({
      employee_id: employeeId,
      date_from: startDate,
      date_to: endDate,
    });
    if (result.success && result.data) {
      setAttendanceRecords(result.data);
      const stats = { present: 0, absent: 0, late: 0, half_day: 0 };
      result.data.forEach((record) => {
        const status = record.status || 'absent';
        if (stats[status] !== undefined) stats[status]++;
      });
      setAttendanceStats(stats);
    }
    const today = await attendanceService.getTodayStatus(employeeId);
    if (today.success) setTodayStatus(today.data);
  }, []);

  const loadViaEssApi = useCallback(async () => {
    const dashResult = await employeeSelfService.getDashboard();
    if (!dashResult.success || !dashResult.data?.employee) {
      return { ok: false, error: dashResult.error };
    }

    const data = dashResult.data;
    const employeeId = data.employee.id;

    const [leavesRes, payslipRes, notifRes, docsRes, lettersRes, holidaysRes, prefsRes] =
      await Promise.all([
        leaveService.getLeaves({ employee_id: employeeId }),
        payrollService.getPayrollRecords({ employee_id: employeeId }),
        notificationService.getNotifications({ employee_id: employeeId, limit: 50 }),
        employeeSelfService.getDocuments(),
        employeeSelfService.getLetters(),
        employeeSelfService.getHolidays(new Date().getFullYear()),
        notificationService.getPreferences(employeeId),
      ]);

    applyPortalData({
      employee: data.employee,
      deployment: data.deployment || null,
      dashboard: data,
      leaves: leavesRes.success ? leavesRes.data || [] : [],
      leaveBalance: leavesRes.success
        ? buildLeaveBalance(leavesRes.data || [])
        : (data.leave_balance || []),
      holidays: holidaysRes.success ? holidaysRes.data || [] : [],
      attendanceRecords: [],
      attendanceStats: data.attendance_stats || { present: 0, absent: 0, late: 0, half_day: 0 },
      todayStatus: data.today || null,
      payslips: payslipRes.success ? payslipRes.data || [] : [],
      documents: docsRes.success ? docsRes.data || [] : [],
      letters: lettersRes.success ? lettersRes.data || { offer_letters: [], increment_letters: [] } : { offer_letters: [], increment_letters: [] },
      notificationCenter: notifRes.success ? notifRes.data || [] : [],
      clientSideMode: false,
    });

    if (prefsRes.success && prefsRes.data) {
      setNotificationPrefs({
        inApp: !!prefsRes.data.in_app,
        email: !!prefsRes.data.email,
        sms: !!prefsRes.data.sms,
        attendanceAlerts: !!prefsRes.data.attendance_alerts,
        payrollAlerts: !!prefsRes.data.payroll_alerts,
        leaveUpdates: !!prefsRes.data.leave_updates,
      });
    }

    await loadAttendance(employeeId);
    return { ok: true };
  }, [applyPortalData, loadAttendance]);

  const loadAll = useCallback(async () => {
    if (!userProfile) return;

    if (isLocalAuth) {
      setEmployee({
        id: userProfile.id || 'local-employee',
        employee_id: 'EMP-LOCAL',
        full_name: userProfile.full_name || 'Employee',
        email: userProfile.email,
        department: userProfile.department || 'General',
        position: 'Employee',
        status: 'active',
      });
      setLoading(false);
      setLastSyncAt(new Date());
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const clientResult = await loadClientSidePortal(userProfile);
      if (clientResult.success) {
        sessionStorage.setItem(ESS_CLIENT_API_KEY, '1');
        applyPortalData(clientResult.data);
        const employeeId = clientResult.data.employee.id;
        const [notifRes, prefsRes] = await Promise.all([
          notificationService.getNotifications({ employee_id: employeeId, limit: 50 }),
          notificationService.getPreferences(employeeId),
        ]);
        if (notifRes.success) setNotificationCenter(notifRes.data || []);
        if (prefsRes.success && prefsRes.data) {
          setNotificationPrefs({
            inApp: !!prefsRes.data.in_app,
            email: !!prefsRes.data.email,
            sms: !!prefsRes.data.sms,
            attendanceAlerts: !!prefsRes.data.attendance_alerts,
            payrollAlerts: !!prefsRes.data.payroll_alerts,
            leaveUpdates: !!prefsRes.data.leave_updates,
          });
        }
        setLastSyncAt(new Date());
        return;
      }

      sessionStorage.removeItem(ESS_CLIENT_API_KEY);
      const ess = await loadViaEssApi();
      if (ess.ok) {
        setLastSyncAt(new Date());
        return;
      }

      setError(clientResult.error || ess.error || 'Employee record not linked to your account. Contact HR.');
      setEmployee(null);
    } catch (err) {
      setError('Failed to load employee portal data');
    } finally {
      setLoading(false);
    }
  }, [userProfile, isLocalAuth, applyPortalData, loadViaEssApi]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const syncNow = useCallback(async () => {
    if (!employee?.id || isLocalAuth) {
      setLastSyncAt(new Date());
      return;
    }
    setSyncing(true);
    try {
      await loadAll();
    } finally {
      setSyncing(false);
    }
  }, [employee, isLocalAuth, loadAll]);

  const reloadLeaves = useCallback(async () => {
    if (!employee?.id) return;
    const data = await fetchEmployeeLeaves(employee.id);
    setLeaves(data.leaves);
    setLeaveBalance(data.leaveBalance);
  }, [employee]);

  const reloadDocuments = useCallback(async () => {
    if (!employee?.id) return;
    const data = await fetchEmployeeDocuments(employee.id);
    setDocuments(data.documents);
    setLetters(data.letters);
  }, [employee]);

  const reloadAttendance = useCallback(async (dateFrom, dateTo) => {
    if (!employee?.id) return null;
    const range = dateFrom && dateTo ? { start: dateFrom, end: dateTo } : monthDateRange();
    const data = await fetchEmployeeAttendance(employee.id, range.start, range.end);
    setAttendanceRecords(data.records);
    setAttendanceStats(data.stats);
    return data;
  }, [employee]);

  const unreadCount = useMemo(
    () => notificationCenter.filter((n) => !n.read_at).length,
    [notificationCenter]
  );

  const value = {
    employee,
    deployment,
    dashboard,
    loading,
    error,
    syncing,
    lastSyncAt,
    isLocalAuth,
    clientSideMode,
    legacyMode: clientSideMode,
    leaves,
    setLeaves,
    leaveBalance,
    holidays,
    attendanceRecords,
    attendanceStats,
    todayStatus,
    payslips,
    documents,
    letters,
    notificationCenter,
    setNotificationCenter,
    notificationPrefs,
    setNotificationPrefs,
    unreadCount,
    loadAll,
    syncNow,
    loadAttendance,
    reloadLeaves,
    reloadDocuments,
    reloadAttendance,
  };

  return (
    <EmployeeSelfServiceContext.Provider value={value}>
      {children}
    </EmployeeSelfServiceContext.Provider>
  );
};

export default EmployeeSelfServiceContext;
